import React, { useContext, useState } from "react";
import CustomTable from "../../components/CustomTable";
import EventAdd from "./EventAdd";
import EventUpdate from "./EventUpdate";
import useApi from "../../hooks/useApi";
import { deleteEvent, ListEvent, updateEvent } from "../../constants/API/API";
import useFilter from "../../hooks/useFilter";
import { AppStateContext } from "../../context/AppContext";
import moment from "moment";
import { Button, DatePicker, Form, message, Modal, Space, Switch, } from "antd";
import { EditOutlined, EyeOutlined, FieldTimeOutlined, } from "@ant-design/icons";
import EventView from "./EventView";
import { useNavigate } from "react-router";
import DeleteRecord from "../../components/DeleteRecord";
import { ShowToast, Severty } from "../../helper/toast";
import useRequest from "../../hooks/useRequest";

const Event = () => {
  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("Event Management");

  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(Date.now());
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [rescheduleModal, setRescheduleModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [data, setData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [filters, setFilters] = useFilter({
    sortField: "createdAt",
    sort: "desc",
    startDate: null,
    endDate: null,
  });
  const navigate = useNavigate();

  const openAddModal = () => setAddModal(true);

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return "";

    const headers = [
      "Title",
      "Address",
      "Capacity",
      "Description",
      "Eligibility",
      "Date & Time",
      "Status",
      "Media",
      "Participants",
    ];

    const escapeForCSV = (value) => {
      if (value === null || value === undefined) return "-";
      const str = String(value).replace(/"/g, '""'); // escape quotes
      return `"${str}"`; // wrap in quotes
    };

    const rows = data.map((record) => {
      const participants =
        record.participants
          ?.map((u) => `${u?.name} ( ${u?.email} )`)
          .join(", ") || "-";

      return [
        escapeForCSV(record.title || "-"),
        escapeForCSV(record.address || "-"),
        escapeForCSV(record.capacity || "-"),
        escapeForCSV(record.description || "-"),
        escapeForCSV(
          `${record.eligibility?.class?.map((cls) => cls?.name)?.join(", ") || "-"} - ${record.eligibility?.belt?.map((blt) => blt?.name)?.join(", ") || "-"
          }`
        ),
        escapeForCSV(moment(record.dateTime).format("lll")),
        escapeForCSV(record.status || "-"),
        escapeForCSV(record.media || "-"),
        escapeForCSV(participants),
      ];
    });

    return [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
  };

  const downloadCSV = (csvData, filename) => {
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExport = () => {
    if (data.length === 0) {
      message.warning("No data available to export");
      return;
    }

    const csvData = convertToCSV(data);
    const filename = `events_${moment().format("YYYYMMDD_HHmmss")}.csv`;
    downloadCSV(csvData, filename);
    message.success("Report exported successfully");
  };

  const renderFilters = () => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
      <Space>
        <Button type="primary" onClick={handleExport}>
          Export Report
        </Button>
      </Space>
    </div>
  );

  useApi({
    callback: ListEvent,
    payload: filters,
    dependencies: [refresh, filters],
    shouldCallApi: true,
    setLoading,
    onSuccess: ({ list, totalDocs }) => {
      setData(list);
      setTotalDocs(totalDocs);
      setLoading(false);
    },
    onError: () => setLoading(false),
  });

  const handleStatusChange = async (checked, belts) => {
    setLoading(true);
    try {
      await updateEvent({ isActive: checked }, belts._id);
      message.success(
        `Event ${checked ? "activated" : "deactivated"} successfully`
      );
      setRefresh(new Date().getTime()); // Refresh list
    } catch (error) {
      message.error("Failed to update status");
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Branch",
      dataIndex: "branch",
      key: "branch",
      render: (branch) => branch?.name || "-",
    },
    {
      title: "Event Type",
      dataIndex: "eventType",
      key: "eventType",
    },
    {
      title: "Exam Date",
      dataIndex: "dateTime",
      key: "examDate",
      render: (dateTime) => (dateTime ? moment(dateTime).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Exam Time",
      dataIndex: "dateTime",
      key: "examTime",
      render: (dateTime) => (dateTime ? moment(dateTime).format("HH:mm") : "-"),
    },
    {
      title: "Registered students / Total capacity",
      key: "registeredCapacity",
      render: (_, record) => {
        const registered = Array.isArray(record?.participants)
          ? record.participants.filter((p) => p && (p.userId || p._id || p)).length
          : (record?.participants?.length || 0);
        const capacity = record?.capacity != null ? record.capacity : "-";
        return `${registered} / ${capacity}`;
      },
    },
    {
      title: "Venue",
      dataIndex: "address",
      key: "address",
      // render: (val) => (val === "video" ? "Video" : "Informational"),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (text, record) => (
        <Switch
          checked={record.isActive}
          onChange={(checked) => handleStatusChange(checked, record)}
        // disabled={!isEdit}
        />
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => moment(text).format("ll"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            onClick={() => {
              setSelectedEventId(record._id);
              setEditModal(true);
            }}
            icon={<EditOutlined />}
            title="Edit"
          />
          <Button
            type="primary"
            onClick={() => {
              setSelectedEventId(record._id);
              navigate(`/event/view/${record._id}`);
            }}
            icon={<EyeOutlined />}
            title="View"
          />
          <Button
            type="primary"
            onClick={() => {
              setSelectedEventId(record._id);
              setRescheduleModal(true);
            }}
            icon={<FieldTimeOutlined />}
            title="View"
          />
          <DeleteRecord
            _id={record._id}
            callback={() => deleteEvent(record._id, { isDeleted: true })}
            setLoading={setLoading}
            setRefresh={setRefresh}
            trigger={true}
            type="Event"
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <CustomTable
        addFn={openAddModal}
        data={data}
        loading={loading}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        totalDocs={totalDocs}
        searchField={["title"]}
        searchPlaceholder="Search by title"
        customFilter={renderFilters}
      />
      {addModal && (
        <EventAdd
          visible={addModal}
          setVisible={setAddModal}
          refresh={setRefresh}
        />
      )}
      {editModal && selectedEventId && (
        <EventUpdate
          editVisible={editModal}
          setEditVisible={setEditModal}
          selectedEventId={selectedEventId}
          refresh={setRefresh}
        />
      )}
      {rescheduleModal && (
        <RescheduleEvent
          visible={rescheduleModal}
          setVisible={setRescheduleModal}
          refresh={() => setRefresh((x) => !x)}
          eventId={selectedEventId}
        />
      )}
    </div>
  );
};

export default Event;

const RescheduleEvent = ({ visible, setVisible, refresh, eventId }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { request } = useRequest();

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
  };

  const handleOk = async (values) => {
    setLoading(true);
    const payload = {
      ...values,
    };

    request({
      url: `admin/event/reschedule/${eventId}`,
      method: "PUT",
      data: payload,
      onSuccess: ({ message }) => {
        ShowToast(message, Severty.SUCCESS);
        setVisible(false);
        setLoading(false);
        refresh();
      },
      onError: (err) => {
        ShowToast(err, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  return (
    <Modal
      open={visible}
      className="tab_modal"
      title="Reschedule Event"
      okText="Submit"
      onCancel={handleCancel}
      okButtonProps={{ form: "Form", htmlType: "submit", loading }}
    >
      <Form id="Form" form={form} onFinish={handleOk} layout="vertical">
        <Form.Item
          name="dateTime"
          label="Date & Time"
          rules={[{ required: true, message: "Please select date and time" }]}
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            style={{ width: "100%" }}
            placeholder="Select Date & Time"
            disabledDate={(current) =>
              current && current < moment().endOf("day")
            } // optional: disable future
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
